import { accessToken } from '../auth/supabase.js'
import { config } from '../config.js'

export class GatewayError extends Error {
  constructor(code, status, detail) {
    super(code || `gateway_${status}`)
    this.name = 'GatewayError'
    this.code = code || 'gateway_error'
    this.status = status
    this.detail = detail
  }
}

export const DEFAULT_GATEWAY_DEADLINE_MS = 15_000

function deadlineSignal(callerSignal, timeoutMs) {
  const controller = new AbortController()
  let timedOut = false
  const abortFromCaller = () => controller.abort(callerSignal?.reason)
  if (callerSignal?.aborted) abortFromCaller()
  else callerSignal?.addEventListener?.('abort', abortFromCaller, { once: true })
  const timer = Number(timeoutMs) > 0
    ? setTimeout(() => {
        timedOut = true
        controller.abort(new DOMException('Gateway deadline exceeded', 'TimeoutError'))
      }, Number(timeoutMs))
    : null
  return {
    signal: controller.signal,
    timedOut: () => timedOut,
    dispose() {
      if (timer) clearTimeout(timer)
      callerSignal?.removeEventListener?.('abort', abortFromCaller)
    },
  }
}

// Authorized byte fetch through the gateway media relay. Browser CORS blocks
// direct reads of presigned provider/R2 URLs; the relay is the sanctioned
// byte channel (allowlisted hosts, auth required, immutable-cached).
export async function gatewayRelayBytes(url, options = {}) {
  const token = await accessToken()
  if (!token) throw new GatewayError('not_signed_in', 401)
  const response = await fetch(`${config.apiBaseUrl}/media/relay?url=${encodeURIComponent(url)}`, {
    headers: { Authorization: `Bearer ${token}` },
    signal: options.signal,
  })
  if (!response.ok) throw new GatewayError(`relay_${response.status}`, response.status, null)
  return response.blob()
}

export async function gatewayRequest(path, options = {}) {
  const token = await accessToken()
  if (!token) throw new GatewayError('not_signed_in', 401)
  return gatewayRequestWithToken(path, options, token)
}

async function gatewayRequestWithToken(path, options, token) {
  const { timeoutMs = DEFAULT_GATEWAY_DEADLINE_MS, signal: callerSignal, ...fetchOptions } = options
  const headers = { ...(fetchOptions.headers || {}), Authorization: `Bearer ${token}` }
  if (fetchOptions.body != null && !headers['Content-Type'] && typeof fetchOptions.body === 'string') headers['Content-Type'] = 'application/json'
  const deadline = deadlineSignal(callerSignal, timeoutMs)
  let response
  try {
    response = await fetch(`${config.apiBaseUrl}${path}`, { ...fetchOptions, headers, signal: deadline.signal })
  } catch (error) {
    if (deadline.timedOut()) {
      throw new GatewayError('gateway_timeout', 408, { path, timeoutMs: Number(timeoutMs) })
    }
    if (callerSignal?.aborted || error?.name === 'AbortError') {
      throw new GatewayError('request_cancelled', 499, { path })
    }
    throw error
  } finally {
    deadline.dispose()
  }
  if (!response.ok) {
    let detail = null
    try { detail = await response.json() } catch {}
    throw new GatewayError(detail?.error || `http_${response.status}`, response.status, detail)
  }
  if (response.status === 204) return null
  return response.json()
}

export const gatewayInternals = Object.freeze({ deadlineSignal, gatewayRequestWithToken })
